import React, { useEffect, useMemo, useRef, useState } from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import {
  FieldHeader,
  FieldErrors,
  FieldHelpText,
  FIELD_SHELL_CLASS,
  FIELD_BARE_INPUT_CLASS,
} from './FieldParts';
import { cn } from '../lib/utils';
import {
  useElementValue,
  useIsEditor,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';

interface Props {
  element: DocumentElementType;
}

type AllowedValues = 'all' | 'positive' | 'negative';

/** Coerce a configForm-stored numeric (always a string) to a finite number or undefined. */
const parseFiniteNumber = (v: unknown): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** Coerce to a non-negative integer or undefined (for decimalPlaces). */
const parseNonNegativeInt = (v: unknown): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.floor(n);
};

/** Count digits after the decimal point in the raw string. */
const countDecimalPlaces = (s: string): number => {
  const dot = s.indexOf('.');
  if (dot === -1) return 0;
  return s.length - dot - 1;
};

const NumberInput: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isEditor = useIsEditor();
  const isReadOnly = useIsReadOnly();
  const [touched, setTouched] = useState(false);
  const data = element.data as {
    label?: string;
    subLabel?: string;
    placeholder?: string;
    required?: boolean;
    helpText?: string;
    hidden?: boolean;
    min?: number | string;
    max?: number | string;
    decimalPlaces?: number | string;
    allowedValues?: AllowedValues;
    step?: number | string;
    defaultValue?: number | string;
  };
  // Normalise to string (NumberInput stores value as string) and only
  // hydrate when the configured default parses to a finite number.
  const defaultValueRaw =
    data?.defaultValue === undefined || data?.defaultValue === null
      ? undefined
      : String(data.defaultValue);
  const defaultValueForHydration =
    defaultValueRaw !== undefined &&
    defaultValueRaw !== '' &&
    Number.isFinite(Number(defaultValueRaw))
      ? defaultValueRaw
      : undefined;
  const displayValue = useDefaultValue(
    value,
    setValue,
    defaultValueForHydration,
  );
  const label = data?.label || 'Number';
  const subLabel = data?.subLabel;
  const placeholder = data?.placeholder || 'Enter number...';
  const required = data?.required || false;
  const helpText = data?.helpText;

  const configuredMin = parseFiniteNumber(data?.min);
  const configuredMax = parseFiniteNumber(data?.max);
  const decimalPlaces = parseNonNegativeInt(data?.decimalPlaces);
  const allowedValues: AllowedValues =
    data?.allowedValues === 'positive' || data?.allowedValues === 'negative'
      ? data.allowedValues
      : 'all';

  // Sign restriction widens the configured min/max if needed: "positive"
  // means the effective floor is at least 0, "negative" means the
  // effective ceiling is at most 0.
  const effectiveMin =
    allowedValues === 'positive'
      ? Math.max(configuredMin ?? 0, 0)
      : configuredMin;
  const effectiveMax =
    allowedValues === 'negative'
      ? Math.min(configuredMax ?? 0, 0)
      : configuredMax;

  const step =
    decimalPlaces !== undefined
      ? decimalPlaces === 0
        ? 1
        : Math.pow(10, -decimalPlaces)
      : undefined;

  // Custom stepper (the native spinner is hidden): nudge by the step, clamp to
  // the effective min/max, and round to the configured decimal places. A
  // pending ref lets rapid synchronous clicks accumulate (setValue has no
  // functional form, so the closed-over displayValue would otherwise be stale);
  // it resets whenever the value changes from elsewhere.
  const pendingRef = useRef<number | null>(null);
  useEffect(() => {
    pendingRef.current = null;
  }, [displayValue]);
  const bump = (dir: 1 | -1) => {
    setTouched(true);
    const amount = step ?? parseFiniteNumber(data?.step) ?? 1;
    const base = pendingRef.current ?? parseFiniteNumber(displayValue) ?? 0;
    let next = base + dir * amount;
    if (effectiveMin !== undefined) next = Math.max(next, effectiveMin);
    if (effectiveMax !== undefined) next = Math.min(next, effectiveMax);
    next = Number(next.toFixed(decimalPlaces ?? 6));
    pendingRef.current = next;
    setValue(String(next));
  };

  const errors = useMemo<string[]>(() => {
    if (isEditor || !touched) return [];
    const out: string[] = [];
    // Coerce defensively: useElementValue is typed `string`, but a resumed
    // draft or server payload can hand back a number, and `(number).trim()`
    // would throw inside this memo.
    const raw = String(value ?? '').trim();
    if (required && raw === '') {
      out.push('This field is required');
    }
    if (raw !== '') {
      const num = Number(raw);
      if (!Number.isFinite(num)) {
        out.push('Must be a number');
      } else {
        if (effectiveMin !== undefined && num < effectiveMin) {
          out.push(`Must be ${effectiveMin} or greater`);
        }
        if (effectiveMax !== undefined && num > effectiveMax) {
          out.push(`Must be ${effectiveMax} or less`);
        }
        if (
          decimalPlaces !== undefined &&
          countDecimalPlaces(raw) > decimalPlaces
        ) {
          out.push(
            decimalPlaces === 0
              ? 'Must be a whole number'
              : `Must have at most ${decimalPlaces} decimal place${
                  decimalPlaces === 1 ? '' : 's'
                }`,
          );
        }
      }
    }
    return Array.from(new Set(out));
  }, [
    isEditor,
    touched,
    required,
    value,
    effectiveMin,
    effectiveMax,
    decimalPlaces,
  ]);

  // Read-only (e.g. a completed result): render the entered number as
  // static text — no input, no validation.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="text-sm">
          {displayValue ? (
            displayValue
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <div className={cn(FIELD_SHELL_CLASS, 'items-stretch')}>
        <input
          id={element.id}
          type="number"
          placeholder={placeholder}
          value={displayValue || ''}
          onChange={(e) => {
            setTouched(true);
            setValue(e.target.value);
          }}
          onBlur={() => setTouched(true)}
          min={effectiveMin}
          max={effectiveMax}
          step={step ?? parseFiniteNumber(data?.step) ?? 'any'}
          className={cn(
            FIELD_BARE_INPUT_CLASS,
            'px-[var(--forms-input-px)] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          )}
        />
        <div className="flex flex-none flex-col border-l border-[var(--forms-border-light)]">
          <button
            type="button"
            tabIndex={-1}
            aria-label="Increment"
            onClick={() => bump(1)}
            className="flex w-[34px] flex-1 items-center justify-center border-b border-[var(--forms-border-light)] text-[var(--forms-muted)] transition-colors hover:bg-[var(--forms-addon-bg)]"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Decrement"
            onClick={() => bump(-1)}
            className="flex w-[34px] flex-1 items-center justify-center text-[var(--forms-muted)] transition-colors hover:bg-[var(--forms-addon-bg)]"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>
      <FieldErrors errors={errors} />
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default NumberInput;
